import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

export interface PlaceSelection {
  formattedAddress: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string | null;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (place: PlaceSelection) => void;
  formatSelectedValue?: (selectedAddress: string, currentValue: string) => string;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
  countries?: string[];
  type?: 'address' | 'regions';
}

interface PlaceSuggestion {
  place_id: string;
  description: string;
}

// Use supabase.functions.invoke so the caller's auth is attached automatically:
// the logged-in user's access token when authenticated, otherwise the public
// anon key — both are project-signed JWTs that satisfy the function's gateway
// `verify_jwt` guard. No secrets are exposed beyond the already-public anon key.
const invokeGooglePlaces = async (body: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke('google-places', { body });
  if (error) {
    // FunctionsHttpError exposes the JSON error payload via context.json().
    const message = await (error as { context?: { json?: () => Promise<{ error?: string }> } })
      .context?.json?.()
      .then((payload) => payload?.error)
      .catch(() => undefined);
    throw new Error(message || error.message || 'Google Places search failed');
  }
  return data;
};

export const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  formatSelectedValue,
  placeholder = 'Start typing an address...',
  className,
  id,
  name,
  countries = ['gb'],
  type = 'address',
}: AddressAutocompleteProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  // Value last set by choosing a suggestion. The debounced search effect skips
  // it so selecting a suggestion doesn't immediately re-open the dropdown with
  // a fresh autocomplete query for the just-selected address.
  const selectedValueRef = useRef<string | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const query = inputValue.trim();

    if (inputValue === selectedValueRef.current) {
      setIsLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;

    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
      setError('');
      return;
    }

    setIsLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const data = await invokeGooglePlaces({ action: 'autocomplete', input: query, countries, type });
        if (requestId !== requestIdRef.current) return;
        setSuggestions(data.suggestions ?? []);
        setShowSuggestions((data.suggestions ?? []).length > 0);
        setError('');
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setSuggestions([]);
        setShowSuggestions(false);
        setError(err instanceof Error ? err.message : 'Google Places search failed');
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [countries, inputValue, type]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    selectedValueRef.current = null;
    setInputValue(newValue);
    onChange(newValue);
  };

  const selectSuggestion = async (suggestion: PlaceSuggestion) => {
    const previousValue = inputValue;
    const provisionalValue = formatSelectedValue
      ? formatSelectedValue(suggestion.description, previousValue)
      : suggestion.description;

    selectedValueRef.current = provisionalValue;
    setInputValue(provisionalValue);
    onChange(provisionalValue);
    setShowSuggestions(false);
    setSuggestions([]);

    try {
      const data = await invokeGooglePlaces({ action: 'details', placeId: suggestion.place_id });
      const place = data.place as PlaceSelection | undefined;
      if (!place) return;
      const formattedAddress = place.formattedAddress || suggestion.description;
      const selectedValue = formatSelectedValue
        ? formatSelectedValue(formattedAddress, previousValue)
        : formattedAddress;
      selectedValueRef.current = selectedValue;
      setInputValue(selectedValue);
      onChange(selectedValue);
      onPlaceSelect?.({ ...place, formattedAddress });
      setError('');
    } catch (err) {
      onPlaceSelect?.({
        formattedAddress: suggestion.description,
        latitude: null,
        longitude: null,
        placeId: suggestion.place_id,
      });
      setError(err instanceof Error ? err.message : 'Could not fetch place details');
    }
  };

  return (
    <div className="relative space-y-1">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          id={id}
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(suggestions.length > 0)}
          onBlur={() => window.setTimeout(() => setShowSuggestions(false), 150)}
          placeholder={placeholder}
          className={cn('pl-9 pr-9', className)}
          autoComplete="off"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type="button"
              className="w-full px-4 py-2 text-left hover:bg-accent hover:text-accent-foreground transition-colors text-sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectSuggestion(suggestion)}
            >
              {suggestion.description}
            </button>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

export default AddressAutocomplete;